/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends HTMLElement {
  constructor() {
    super();
    this.isExpanded = false;
    /** @type {Element[]} */
    this.hiddenElements = [];
  }

  connectedCallback() {
    this.querySelectorAll('table').forEach(this.#formatTable);

    if (this.readMoreLimit) {
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => this.#initReadMore());
    }
  }

  get readMoreLimit() {
    const attr = this.getAttribute('read-more-limit');
    return attr ? parseInt(attr) : null;
  }

  get button() {
    return this.querySelector('.read-more__button button');
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  #initReadMore() {
    const elements = Array.from(this.children).filter(el =>
      !el.classList.contains('read-more__button')
    );

    // Check if we have more elements than the limit
    if (!this.readMoreLimit || elements.length <= this.readMoreLimit) {
      // Show all elements if no limiting needed and hide the button
      elements.forEach(el => {
        /** @type {HTMLElement} */ (el).style.display = '';
      });
      const button = this.querySelector('.read-more__button');
      if (button) /** @type {HTMLElement} */ (button).style.display = 'none';
      return;
    }

    // Elements beyond the limit (already hidden by CSS)
    this.hiddenElements = elements.slice(this.readMoreLimit);
    this.hiddenElements.forEach(el => {
      el.setAttribute('aria-hidden', 'true');
    });

    this.classList.add('read-more--init');
    this.button?.addEventListener('click', this.toggle.bind(this));
  }

  toggle() {
    this.isExpanded = true;

    this.hiddenElements.forEach(el => {
      /** @type {HTMLElement} */ (el).style.display = '';
      el.removeAttribute('aria-hidden');
    });

    this.classList.add('read-more--open');

    // Hide the button once expanded
    const buttonContainer = this.querySelector('.read-more__button');
    if (buttonContainer) {
      /** @type {HTMLElement} */ (buttonContainer).style.display = 'none';
    }
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
